<template>
  <div dir="rtl" class="mr-8 px-4 sm:px-6 lg:px-8">
    <!-- Alert -->
    <div v-if="alert.show" 
         :class="[
           'mb-6 p-4 rounded-md border',
           alert.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : '',
           alert.type === 'danger' ? 'bg-red-50 border-red-200 text-red-800' : '',
           alert.type === 'warning' ? 'bg-yellow-50 border-yellow-200 text-yellow-800' : ''
         ]">
      {{ alert.message }}
    </div>

    <!-- Page Header -->
    <div class="sm:flex sm:items-center">
      <div class="sm:flex-auto">
        <h1 class="text-xl font-semibold text-gray-900">إدارة المدراء</h1>
        <p class="mt-2 text-sm text-gray-700">إدارة حسابات جميع أنواع المدراء (Admin, General Manager, Super Admin)</p>
      </div>
      <div class="mt-4 sm:mt-0 sm:mr-16 sm:flex-none">
        <button
          @click="openAddModal"
          class="px-4 py-2 bg-customHafadhColor text-white rounded hover:bg-customHafadhColor/90 transition-colors">
          إضافة مدير جديد
        </button>
      </div>
    </div>

    <!-- Search and Filter Section -->
    <div class="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
      <div class="relative">
        <input
          v-model="searchTerm"
          type="text"
          placeholder="البحث في المدراء..."
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-3"
        />
      </div>
      <div class="relative">
        <select
          v-model="filterTenant"
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        >
          <option :value="null">جميع الفروع</option>
          <option v-for="tenant in tenants" :key="tenant.id" :value="tenant.id">
            {{ tenant.name }}
          </option>
        </select>
      </div>
      <div class="relative">
        <select
          v-model="filterRole"
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        >
          <option :value="null">جميع الأنواع</option>
          <option value="ADMIN">مدير</option>
          <option value="GENERAL_MANAGER">مدير عام</option>
          <option value="SUPER_ADMIN">مدير النظام</option>
        </select>
      </div>
      <div class="relative">
        <select
          v-model="filterEnabled"
          class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
        >
          <option :value="null">جميع الحالات</option>
          <option :value="true">مفعل فقط</option>
          <option :value="false">معطل فقط</option>
        </select>
      </div>
      <div class="text-sm text-gray-600 bg-blue-50 px-3 py-2 rounded-lg flex items-center">
        إجمالي المدراء: {{ managers.length }}
      </div>
    </div>

    <!-- Managers Table -->
    <div class="mt-8 flex flex-col">
      <div class="sm:flex-auto">
        <h1 class="text-lg font-medium text-gray-900">قائمة المدراء</h1>
        <p class="mt-2 text-xs text-gray-700"></p>
      </div>

      <div v-if="loading" class="text-center py-12">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <p class="mt-2 text-gray-600">جاري التحميل...</p>
      </div>

      <div v-else-if="filteredManagers.length === 0" class="text-center py-12">
        <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"></path>
        </svg>
        <p class="mt-2 text-gray-600">
          {{ searchTerm || filterTenant || filterEnabled !== null ? 'لا يوجد مدراء فروع تطابق البحث' : 'لا يوجد مدراء فروع مسجلين' }}
        </p>
      </div>

      <div v-else class="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
        <div class="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
          <div class="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
            <table class="min-w-full divide-y divide-customHafadhColor">
              <thead class="bg-customHafadhColor text-center">
                <tr>
                  <th class="py-3.5 pr-4 pl-3 text-right text-sm font-semibold text-gray-900 sm:pr-6">#</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">الاسم</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">البريد الإلكتروني</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">الهاتف</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">الفرع</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">نوع المدير</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">الحالة</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">تعديل</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">تعطيل/تفعيل</th>
                  <th class="px-3 py-3.5 text-right text-sm font-semibold text-gray-900">حذف</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200 bg-white">
                <tr v-for="(manager, index) in filteredManagers" :key="manager.id">
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm text-gray-900 sm:pr-6">{{ index + 1 }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">{{ manager.fullName }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ manager.email }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ manager.phone || '-' }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{{ manager.tenantName || '-' }}</td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm">
                    <span 
                      :class="[
                        'px-2 py-1 rounded-full text-xs font-medium',
                        manager.roleCode === 'SUPER_ADMIN' ? 'bg-purple-100 text-purple-800' : '',
                        manager.roleCode === 'GENERAL_MANAGER' ? 'bg-blue-100 text-blue-800' : '',
                        manager.roleCode === 'ADMIN' ? 'bg-green-100 text-green-800' : '',
                      ]">
                      {{ manager.roleName || manager.roleCode || '-' }}
                    </span>
                  </td>
                  <td class="whitespace-nowrap px-3 py-4 text-sm">
                    <span 
                      :class="[
                        'px-2 py-1 rounded-full text-xs font-medium',
                        manager.enabled ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      ]">
                      {{ manager.enabled ? 'مفعل' : 'معطل' }}
                    </span>
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button class="enabled-button" @click="openEditModal(manager)">تعديل</button>
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button 
                      v-if="manager.enabled"
                      class="text-orange-600 hover:text-orange-800" 
                      @click="handleDisable(manager.id)">
                      تعطيل
                    </button>
                    <button 
                      v-else
                      class="text-green-600 hover:text-green-800" 
                      @click="handleEnable(manager.id)">
                      تفعيل
                    </button>
                  </td>
                  <td class="whitespace-nowrap py-4 pr-4 pl-3 text-sm font-medium sm:pr-6">
                    <button 
                      class="text-red-600 hover:text-red-800" 
                      @click="handleDelete(manager.id)">
                      حذف
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination.totalPages > 1" class="mt-6 flex items-center justify-between">
        <div class="text-sm text-gray-700">
          عرض {{ pagination.from }} إلى {{ pagination.to }} من {{ pagination.total }} نتائج
        </div>
        <div class="flex gap-2">
          <button
            @click="changePage(pagination.currentPage - 1)"
            :disabled="pagination.currentPage === 1"
            class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
            السابق
          </button>
          <button
            @click="changePage(pagination.currentPage + 1)"
            :disabled="pagination.currentPage === pagination.totalPages"
            class="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
            التالي
          </button>
        </div>
      </div>
    </div>

    <!-- Add/Edit Modal -->
    <div v-if="showModal" class="popup" dir="rtl">
      <div class="popup-content" style="max-width: 600px; max-height: 90vh; overflow-y: auto;">
        <h3 class="text-lg leading-6 font-medium text-gray-900 mb-4">
          {{ editMode ? 'تعديل المدير' : 'إضافة مدير جديد' }}
        </h3>
        
        <form @submit.prevent="handleSave">
          <div class="grid grid-cols-1 gap-4">
            <div>
              <label for="fullName" class="block text-sm font-medium text-gray-700 mb-2">الاسم الكامل *</label>
              <input
                v-model="formData.fullName"
                type="text"
                id="fullName"
                required
                placeholder="أدخل الاسم الكامل"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label for="email" class="block text-sm font-medium text-gray-700 mb-2">البريد الإلكتروني *</label>
              <input
                v-model="formData.email"
                type="email"
                id="email"
                required
                placeholder="example@email.com"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label for="password" class="block text-sm font-medium text-gray-700 mb-2">
                كلمة المرور {{ editMode ? '(اتركها فارغة إن لم تتغير)' : '*' }}
              </label>
              <input
                v-model="formData.password"
                type="password"
                id="password"
                :required="!editMode"
                :minlength="editMode ? 0 : 8"
                placeholder="أدخل كلمة المرور"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label for="phone" class="block text-sm font-medium text-gray-700 mb-2">رقم الهاتف</label>
              <input
                v-model="formData.phone"
                type="tel"
                id="phone"
                placeholder="أدخل رقم الهاتف"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>

            <div>
              <label for="tenantId" class="block text-sm font-medium text-gray-700 mb-2">الفرع *</label>
              <select
                v-model="formData.tenantId"
                id="tenantId"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">اختر الفرع</option>
                <option v-for="tenant in tenants" :key="tenant.id" :value="tenant.id">
                  {{ tenant.name }}
                </option>
              </select>
            </div>

            <div>
              <label for="roleCode" class="block text-sm font-medium text-gray-700 mb-2">نوع المدير *</label>
              <select
                v-model="formData.roleCode"
                id="roleCode"
                required
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              >
                <option value="">اختر نوع المدير</option>
                <option value="ADMIN">مدير</option>
                <option value="GENERAL_MANAGER">مدير عام</option>
                <option value="SUPER_ADMIN">مدير النظام</option>
              </select>
            </div>

            <div v-if="editMode">
              <label class="flex items-center">
                <input
                  v-model="formData.enabled"
                  type="checkbox"
                  class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span class="mr-2 text-sm text-gray-700">مفعل</span>
              </label>
            </div>
          </div>

          <div class="mt-6 grid grid-cols-2 gap-3">
            <button
              type="submit"
              :disabled="saving"
              class="enabled-button w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm disabled:opacity-50">
              {{ saving ? 'جاري الحفظ...' : 'حفظ' }}
            </button>
            <button
              type="button"
              @click="closeModal"
              class="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:text-sm">
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import url from '@/url.js'

export default {
  name: 'GeneralManagersManagement',
  data() {
    return {
      managers: [],
      tenants: [],
      loading: false,
      saving: false,
      searchTerm: '',
      filterTenant: null,
      filterRole: null,
      filterEnabled: null,
      showModal: false,
      editMode: false,
      formData: {
        id: null,
        fullName: '',
        email: '',
        password: '',
        phone: '',
        tenantId: '',
        enabled: true,
      },
      alert: {
        show: false,
        type: 'success',
        message: '',
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        total: 0,
        from: 0,
        to: 0,
      },
    }
  },
  computed: {
    filteredManagers() {
      let filtered = this.managers

      if (this.searchTerm) {
        const search = this.searchTerm.toLowerCase()
        filtered = filtered.filter(manager => 
          manager.fullName?.toLowerCase().includes(search) ||
          manager.email?.toLowerCase().includes(search)
        )
      }

      if (this.filterTenant) {
        filtered = filtered.filter(manager => manager.tenantId === this.filterTenant)
      }

      if (this.filterRole) {
        filtered = filtered.filter(manager => manager.roleCode === this.filterRole)
      }

      if (this.filterEnabled !== null) {
        filtered = filtered.filter(manager => manager.enabled === this.filterEnabled)
      }

      return filtered
    }
  },
  watch: {
    searchTerm() {
      this.pagination.currentPage = 1
    },
    filterTenant() {
      this.pagination.currentPage = 1
      this.loadManagers()
    },
    filterRole() {
      this.pagination.currentPage = 1
      this.loadManagers()
    },
    filterEnabled() {
      this.pagination.currentPage = 1
      this.loadManagers()
    },
  },
  mounted() {
    this.loadTenants()
    this.loadManagers()
  },
  methods: {
    async loadTenants() {
      try {
        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.tenants_list,
          {
            params: { size: 1000 },
            headers: { 'X-Authorization': localStorage.getItem('token') }
          }
        )

        let responseData = null
        if (response.data?.result?.data) {
          responseData = response.data.result.data
        } else if (response.data?.data) {
          responseData = response.data.data
        } else if (response.data?.result) {
          responseData = response.data.result
        } else {
          responseData = response.data
        }

        if (responseData) {
          if (responseData.pagination) {
            this.tenants = Array.isArray(responseData.data) ? responseData.data : []
          } else if (responseData.content !== undefined) {
            this.tenants = Array.isArray(responseData.content) ? responseData.content : []
          } else if (Array.isArray(responseData)) {
            this.tenants = responseData
          } else {
            this.tenants = []
          }
        } else {
          this.tenants = []
        }
      } catch (error) {
        console.error('Failed to load tenants', error)
        this.showAlert('danger', 'فشل في تحميل قائمة الفروع')
      }
    },
    async loadManagers() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.currentPage,
          size: 10,
          search: this.searchTerm || undefined,
          tenantId: this.filterTenant || undefined,
          roleCode: this.filterRole || undefined,
          enabled: this.filterEnabled !== null ? this.filterEnabled : undefined,
        }

        const response = await axios.get(
          process.env.VUE_APP_SERVICE_URL + url.general_managers_list,
          {
            params,
            headers: { 'X-Authorization': localStorage.getItem('token') }
          }
        )

        let responseData = null
        if (response.data?.result?.data) {
          responseData = response.data.result.data
        } else if (response.data?.data) {
          responseData = response.data.data
        } else if (response.data?.result) {
          responseData = response.data.result
        } else {
          responseData = response.data
        }

        if (responseData) {
          if (responseData.pagination) {
            this.managers = Array.isArray(responseData.data) ? responseData.data : []
            const pag = responseData.pagination
            this.pagination = {
              currentPage: pag.current_page || 1,
              totalPages: pag.last_page || 1,
              total: pag.total || 0,
              from: pag.from || 0,
              to: pag.to || 0,
            }
          } else if (responseData.content !== undefined) {
            this.managers = Array.isArray(responseData.content) ? responseData.content : []
            this.pagination = {
              currentPage: responseData.number + 1 || 1,
              totalPages: responseData.totalPages || 1,
              total: responseData.totalElements || 0,
              from: (responseData.number || 0) * (responseData.size || 10) + 1,
              to: Math.min((responseData.number + 1 || 1) * (responseData.size || 10), responseData.totalElements || 0)
            }
          } else if (Array.isArray(responseData)) {
            this.managers = responseData
            this.pagination = {
              currentPage: 1,
              totalPages: 1,
              total: responseData.length,
              from: 1,
              to: responseData.length
            }
          } else {
            this.managers = []
          }
        } else {
          this.managers = []
        }
      } catch (error) {
        console.error('Failed to load general managers', error)
        this.showAlert('danger', error.response?.data?.mesAr || 'فشل في تحميل مدراء الفروع')
      } finally {
        this.loading = false
      }
    },
    openAddModal() {
      this.editMode = false
      this.formData = {
        id: null,
        fullName: '',
        email: '',
        password: '',
        phone: '',
        tenantId: '',
        enabled: true,
      }
      this.showModal = true
    },
    openEditModal(manager) {
      this.editMode = true
      this.formData = {
        id: manager.id,
        fullName: manager.fullName,
        email: manager.email,
        password: '',
        phone: manager.phone || '',
        tenantId: manager.tenantId,
        roleCode: manager.roleCode || '',
        enabled: manager.enabled,
      }
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
      this.editMode = false
      this.formData = {
        id: null,
        fullName: '',
        email: '',
        password: '',
        phone: '',
        tenantId: '',
        roleCode: '',
        enabled: true,
      }
    },
    async handleSave() {
      // Validate required fields
      if (!this.formData.fullName || !this.formData.email || !this.formData.tenantId || !this.formData.roleCode) {
        this.showAlert('danger', 'الرجاء إدخال جميع الحقول المطلوبة')
        this.saving = false
        return
      }

      if (!this.editMode && !this.formData.password) {
        this.showAlert('danger', 'الرجاء إدخال كلمة المرور')
        this.saving = false
        return
      }

      this.saving = true
      try {
        const payload = {
          fullName: this.formData.fullName.trim(),
          email: this.formData.email.trim().toLowerCase(),
          phone: this.formData.phone?.trim() || null,
          tenantId: this.formData.tenantId.trim(), // Ensure it's not empty
          roleCode: this.formData.roleCode.trim(),
        }

        if (this.formData.password && this.formData.password.trim()) {
          payload.password = this.formData.password.trim()
        }

        if (this.editMode) {
          payload.enabled = this.formData.enabled
        }

        // Log payload for debugging
        console.log('Saving manager with payload:', payload)

        if (this.editMode) {
          await axios.put(
            process.env.VUE_APP_SERVICE_URL + url.general_managers_update + this.formData.id,
            payload,
            {
              headers: { 'X-Authorization': localStorage.getItem('token') }
            }
          )
          this.showAlert('success', 'تم تحديث مدير الفرع بنجاح')
        } else {
          // Ensure password is provided for new managers
          if (!payload.password || payload.password.length < 8) {
            this.showAlert('danger', 'كلمة المرور يجب أن تكون 8 أحرف على الأقل')
            this.saving = false
            return
          }

          await axios.post(
            process.env.VUE_APP_SERVICE_URL + url.general_managers_create,
            payload,
            {
              headers: { 
                'X-Authorization': localStorage.getItem('token'),
                'Content-Type': 'application/json'
              }
            }
          )
          this.showAlert('success', 'تم إنشاء مدير الفرع بنجاح')
        }

        this.closeModal()
        this.loadManagers()
      } catch (error) {
        console.error('Failed to save general manager', error)
        this.showAlert('danger', error.response?.data?.mesAr || 'فشل في حفظ مدير الفرع')
      } finally {
        this.saving = false
      }
    },
    async handleDelete(userId) {
      if (!confirm('هل أنت متأكد من حذف مدير الفرع هذا؟')) return

      try {
        await axios.delete(
          process.env.VUE_APP_SERVICE_URL + url.general_managers_delete + userId,
          {
            headers: { 'X-Authorization': localStorage.getItem('token') }
          }
        )
        this.showAlert('success', 'تم حذف مدير الفرع بنجاح')
        this.loadManagers()
      } catch (error) {
        console.error('Failed to delete general manager', error)
        this.showAlert('danger', error.response?.data?.mesAr || 'فشل في حذف مدير الفرع')
      }
    },
    async handleDisable(userId) {
      if (!confirm('هل أنت متأكد من تعطيل مدير الفرع هذا؟')) return

      try {
        await axios.post(
          process.env.VUE_APP_SERVICE_URL + url.general_managers_disable + userId,
          null,
          {
            headers: { 'X-Authorization': localStorage.getItem('token') }
          }
        )
        this.showAlert('success', 'تم تعطيل مدير الفرع بنجاح')
        this.loadManagers()
      } catch (error) {
        console.error('Failed to disable general manager', error)
        this.showAlert('danger', error.response?.data?.mesAr || 'فشل في تعطيل مدير الفرع')
      }
    },
    async handleEnable(userId) {
      try {
        await axios.post(
          process.env.VUE_APP_SERVICE_URL + url.general_managers_enable + userId,
          null,
          {
            headers: { 'X-Authorization': localStorage.getItem('token') }
          }
        )
        this.showAlert('success', 'تم تفعيل مدير الفرع بنجاح')
        this.loadManagers()
      } catch (error) {
        console.error('Failed to enable general manager', error)
        this.showAlert('danger', error.response?.data?.mesAr || 'فشل في تفعيل مدير الفرع')
      }
    },
    changePage(page) {
      if (page >= 1 && page <= this.pagination.totalPages) {
        this.pagination.currentPage = page
        this.loadManagers()
      }
    },
    showAlert(type, message) {
      this.alert = { show: true, type, message }
      setTimeout(() => {
        this.alert.show = false
      }, 5000)
    },
  },
}
</script>

<style scoped>
.enabled-button {
  @apply px-4 py-2 bg-customHafadhColor text-white rounded hover:bg-customHafadhColor/90 w-20 h-10;
}

.popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.popup-content {
  background-color: #fff;
  padding: 24px;
  border-radius: 8px;
  width: 90%;
  max-width: 600px;
  text-align: right;
}
</style>

